/**
 * FP3-062 — Inline rename dialog for the student-workspace header.
 *
 * Surfaced ONLY for callers permitted to rewrite canonical identity:
 *   - super_admin
 *   - admin / org_admin
 *   - solo provider OWNER (account_type=individual_provider)
 *
 * School-team providers + assistants never see the affordance.
 * Read-only / shadow / oversight modes hide it too.
 *
 * Saves via PATCH /api/students/{id}. The backend still enforces the
 * permission gate as the source of truth — this dialog is the UX
 * mirror of that backend rule.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
// FP3-122A — Broadcast the rename across steady-state caches.
import { invalidateAfterMutation } from "@/lib/adminQueries";

export default function StudentRenameDialog({ open, onOpenChange, student, onSaved }) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [saving, setSaving] = useState(false);

  // Seed the form whenever the dialog is opened or the student changes.
  useEffect(() => {
    if (open && student) {
      setFirstName(student.first_name || "");
      setLastName(student.last_name || "");
    }
  }, [open, student]);

  const close = () => {
    if (saving) return;
    onOpenChange?.(false);
  };

  const save = async () => {
    const trimmedFirst = (firstName || "").trim();
    const trimmedLast = (lastName || "").trim();
    if (!trimmedFirst || !trimmedLast) {
      toast.error("Both first and last name are required");
      return;
    }
    if (
      trimmedFirst === (student.first_name || "") &&
      trimmedLast === (student.last_name || "")
    ) {
      toast.message("No changes to save");
      onOpenChange?.(false);
      return;
    }
    setSaving(true);
    try {
      await api.updateStudentDemographics(student.id, {
        first_name: trimmedFirst,
        last_name: trimmedLast,
      });
      // FP3-122A — Broadcast the identity change across the app so
      // every steady-state cache that carries a student label refreshes:
      // Admin Students list + summary, Student Assignments, Admin
      // Progress, Reports, Admin Dashboard tiles. Non-cached surfaces
      // (Provider Caseload, Schedule Builder, Calendar) re-fetch on
      // their next mount anyway.
      try {
        invalidateAfterMutation.studentEdited();
      } catch (_e) {
        // Cache invalidation is best-effort — never block the save
        // toast on a query-client edge case.
      }
      toast.success("Student name updated");
      onOpenChange?.(false);
      onSaved?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || "Save failed";
      toast.error(detail);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        className="bg-white"
        data-testid="student-rename-dialog"
      >
        <DialogHeader>
          <DialogTitle data-testid="student-rename-dialog-title">
            Edit student name
          </DialogTitle>
          <DialogDescription>
            Updates the canonical first and last name everywhere this student
            appears. Other fields are unchanged.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="student-rename-first">First name</Label>
            <Input
              id="student-rename-first"
              data-testid="student-rename-first-input"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              maxLength={80}
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="student-rename-last">Last name</Label>
            <Input
              id="student-rename-last"
              data-testid="student-rename-last-input"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              maxLength={80}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={close}
            disabled={saving}
            data-testid="student-rename-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={save}
            disabled={saving}
            className="bg-[#1E3F33] hover:bg-[#173127] text-white"
            data-testid="student-rename-save"
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
